import { DDSTypes } from "src/modules/3rd-party-management/constants";
import { INTERNATIONAL_DATE_FORMAT } from "src/common/constants";

const joinParts = (parts = [], separator = ", ") =>
    parts.filter((part) => part != null && String(part).trim() !== "").join(separator);

const resolveCountryName = (country) => {
    if (!country) {
        return null;
    }

    if (typeof country === "string") {
        return country;
    }

    return country?.name || country?.label || null;
};

const formatSingleGeoCoordinates = (reference) => {
    const geoJson = reference?.geoJSON || reference;
    const coordinates = geoJson?.geometry?.coordinates;

    if (!coordinates) {
        return null;
    }

    if (geoJson.geometry.type === "Point" && Array.isArray(coordinates)) {
        return `${coordinates[1]}, ${coordinates[0]}`;
    }

    if (geoJson.geometry.type === "Polygon" && Array.isArray(coordinates?.[0]?.[0])) {
        const [longitude, latitude] = coordinates[0][0];
        return `${latitude}, ${longitude}`;
    }

    return null;
};

const formatGeolocationCoordinates = (geolocationReference = []) => {
    if (!Array.isArray(geolocationReference) || !geolocationReference.length) {
        return null;
    }

    const firstCoordinates = formatSingleGeoCoordinates(geolocationReference[0]);

    if (geolocationReference.length === 1) {
        return firstCoordinates || "1 location";
    }

    if (firstCoordinates) {
        return `${firstCoordinates} (+${geolocationReference.length - 1} more)`;
    }

    return `${geolocationReference.length} location(s)`;
};

const formatProductionArea = (product = {}) => {
    if (product?.productionArea) {
        return product.productionArea;
    }

    const geolocationReference = product?.geolocationReference;

    if (!Array.isArray(geolocationReference) || !geolocationReference.length) {
        return null;
    }

    const namedAreas = geolocationReference
        .map((reference) => reference?.name || reference?.label || reference?.areaName)
        .filter(Boolean);

    if (namedAreas.length) {
        return namedAreas.join(", ");
    }

    return `${geolocationReference.length} mapped area(s)`;
};

const formatCoordinates = (product = {}) => {
    if (product?.coordinates) {
        return product.coordinates;
    }

    return formatGeolocationCoordinates(product?.geolocationReference);
};

const formatDdsDeclarationType = (declarationType, t = (value) => value) => {
    if (!declarationType) {
        return null;
    }

    return t(DDSTypes[declarationType]?.title || declarationType);
};

const formatStatementValidation = (product = {}, t = (value) => value) => {
    if (product?.ddsDeclarationType !== "provide_reference_numbers") {
        return null;
    }

    if (product?.statementValidation === true) {
        return t("Valid");
    }

    if (product?.statementValidation === false) {
        return t("Invalid");
    }

    return t("Validating");
};

const formatGeoStatus = (product = {}, t = (value) => value) => {
    if (Array.isArray(product?.geolocationReference) && product.geolocationReference.length) {
        const count = product.geolocationReference.length;
        return count > 1 ? `${t("Provided")} (${count})` : t("Provided");
    }

    return t("Not Provided");
};

const formatProductName = (product = {}) => {
    return product?.productName || product?.name?.name || null;
};

const formatNaceCode = (product = {}) => {
    const productName = product?.name;

    if (!productName) {
        return null;
    }

    if (productName.naceCode) {
        return productName.naceCode;
    }

    if (productName.class && productName.name) {
        return `${productName.class} - ${productName.name}`;
    }

    return productName.class || null;
};

const formatDateValue = (dateUtils, value) => {
    if (!value) {
        return null;
    }

    if (!dateUtils) {
        return value;
    }

    return dateUtils.convertDateToDate(value, INTERNATIONAL_DATE_FORMAT);
};

const formatTradeInfo = (dateValue, countryName) =>
    joinParts([dateValue, countryName], " / ");

const formatCustomsCodes = (product = {}) =>
    joinParts([
        product?.hsCode ? `HS: ${product.hsCode}` : null,
        product?.knCode ? `KN: ${product.knCode}` : null,
        product?.taricCode ? `TARIC: ${product.taricCode}` : null,
    ], "; ");

const formatDdsNumbers = (product = {}) =>
    joinParts([
        product?.ddsReferenceNumber ? `Ref: ${product.ddsReferenceNumber}` : null,
        product?.ddsVerificationNumber ? `Ver: ${product.ddsVerificationNumber}` : null,
    ], "; ");

const formatLogistics = (product = {}) =>
    joinParts([
        product?.containerNumber ? `C: ${product.containerNumber}` : null,
        product?.shipmentNumber ? `S: ${product.shipmentNumber}` : null,
    ], "; ");

export const PRODUCT_SUMMARY_COLUMNS = [
    { key: "index", label: "#", width: "6%" },
    { key: "name", label: "Name", width: "28%" },
    { key: "nace", label: "NACE", width: "18%" },
    { key: "lot", label: "Lot", width: "16%" },
    { key: "description", label: "Description", width: "32%" },
];

export const PRODUCT_DETAIL_IDENTITY_TRADE_COLUMNS = [
    { key: "index", label: "#", width: "4%" },
    { key: "id", label: "ID", width: "7%" },
    { key: "name", label: "Name", width: "12%" },
    { key: "nace", label: "NACE", width: "9%" },
    { key: "lot", label: "Lot", width: "8%" },
    { key: "internalId", label: "Internal ID", width: "9%" },
    { key: "importInfo", label: "Import", width: "11%" },
    { key: "exportInfo", label: "Export", width: "11%" },
    { key: "quantity", label: "Qty (t)", width: "6%" },
    { key: "logistics", label: "Logistics", width: "10%" },
    { key: "description", label: "Description", width: "13%" },
];

export const PRODUCT_DETAIL_CODES_DDS_COLUMNS = [
    { key: "index", label: "#", width: "5%" },
    { key: "id", label: "ID", width: "8%" },
    { key: "name", label: "Name", width: "12%" },
    { key: "codes", label: "HS / KN / TARIC", width: "14%" },
    { key: "origin", label: "Origin", width: "10%" },
    { key: "eudr", label: "EUDR", width: "7%" },
    { key: "ddsType", label: "DDS Type", width: "12%" },
    { key: "ddsNumbers", label: "DDS Numbers", width: "14%" },
    { key: "statementStatus", label: "Status", width: "8%" },
    { key: "geoStatus", label: "Geo", width: "10%" },
];

export const DUE_DILIGENCE_COLUMNS = [
    { key: "index", label: "#", width: "5%" },
    { key: "id", label: "ID", width: "10%" },
    { key: "commodity", label: "Commodity", width: "18%" },
    { key: "origin", label: "Country of Origin", width: "14%" },
    { key: "productionArea", label: "Production Area", width: "20%" },
    { key: "coordinates", label: "Coordinates", width: "18%" },
    { key: "geoStatus", label: "Geo Status", width: "15%" },
];

const buildProductSummaryRecords = (products = []) => {
    if (!Array.isArray(products)) {
        return [];
    }

    return products.map((product, index) => ({
        id: product?.id || `product-summary-${index}`,
        index: index + 1,
        name: formatProductName(product),
        nace: formatNaceCode(product),
        lot: product?.lotNumber,
        description: product?.description,
    }));
};

const buildProductDetailRecords = (products = [], { dateUtils, t = (value) => value, supplierCountry } = {}) => {
    if (!Array.isArray(products)) {
        return [];
    }

    return products.map((product, index) => {
        const importDate = formatDateValue(dateUtils, product?.importDate || product?.createdAt);
        const exportDate = formatDateValue(dateUtils, product?.exportDate);

        return {
            id: product?.id || `product-detail-${index}`,
            index: index + 1,
            name: formatProductName(product),
            nace: formatNaceCode(product),
            lot: product?.lotNumber,
            internalId: product?.internalProductId,
            importInfo: formatTradeInfo(
                importDate,
                resolveCountryName(product?.importFromCountry) || supplierCountry
            ),
            exportInfo: formatTradeInfo(
                exportDate,
                resolveCountryName(product?.exportToCountry)
            ),
            quantity: product?.metricTonnes,
            logistics: formatLogistics(product),
            description: product?.description,
            codes: formatCustomsCodes(product),
            origin: resolveCountryName(product?.originCountry) || supplierCountry,
            eudr: product?.is_eudr ? t("Yes") : t("No"),
            ddsType: formatDdsDeclarationType(product?.ddsDeclarationType, t),
            ddsNumbers: formatDdsNumbers(product),
            statementStatus: formatStatementValidation(product, t),
            geoStatus: formatGeoStatus(product, t),
        };
    });
};

const buildDueDiligenceRecords = (products = [], { t = (value) => value, supplierCountry } = {}) => {
    if (!Array.isArray(products)) {
        return [];
    }

    return products.map((product, index) => ({
        id: product?.id || `product-dds-${index}`,
        index: index + 1,
        commodity: formatProductName(product),
        origin: resolveCountryName(product?.originCountry) || supplierCountry,
        productionArea: formatProductionArea(product),
        coordinates: formatCoordinates(product),
        geoStatus: formatGeoStatus(product, t),
    }));
};

export {
    buildProductDetailRecords,
    buildDueDiligenceRecords,
    buildProductSummaryRecords,
    formatProductName,
};
